import { FC, useEffect, useState } from 'react';
import theme from 'config/theme';
import Spacing from 'components/Spacing';
import { ENV_CONFIG } from 'config/environment';
import { ButtonBrand } from 'components/ButtonBrand';
import { Eyebrow } from 'components/Typography';
import { GradientBorderCard } from 'components/Cards/GradientBorderCard';
import { getPathWithRef } from 'helpers/utils';
import { getFundingGapAPI } from 'store/funding_gap/actions';
import { Counter } from './Counter';
import { AutopilotCounter } from './AutopilotCounter';

const STARTING_GAP = 64100000;

interface Props {
  isAutopilot?: boolean;
  onSelectIssue?: (issueKey: string) => void;
  committed_amount?: number;
  issues?: any[];
}

export const DollarCountCard: FC<Props> = ({
  isAutopilot = false,
  onSelectIssue,
  committed_amount,
  issues,
}) => {
  const [gapAmountEstimate, setGapAmountEstimate] = useState<number | null>(null);
  const [isGapUpdated, setIsGapUpdated] = useState(false);
  const [smallCounterStarted, setSmallCounterStarted] = useState(false);
  const [bigCounterStarted, setBigCounterStarted] = useState(false);

  useEffect(() => {
    if (isAutopilot) {
      return;
    }
    getFundingGapAPI()
      .then(response => {
        setGapAmountEstimate(parseFloat(response.data.current_gap_amount_estimate));
        setIsGapUpdated(true);
      })
      .catch(() => {
        setGapAmountEstimate(null);
      });
  }, []);

  const decreaseAmount = (maxAmount: number, interval: number, minGapAmount = 0) => {
    setTimeout(() => {
      const random = Math.floor(Math.random() * maxAmount) + 1;
      if (!gapAmountEstimate || gapAmountEstimate <= minGapAmount) {
        return;
      }
      setGapAmountEstimate(amount =>
        amount ? (amount - random > minGapAmount ? amount - random : minGapAmount) : null
      );
      decreaseAmount(maxAmount, interval, minGapAmount);
    }, interval);
  };

  useEffect(() => {
    if (!isGapUpdated) {
      return;
    }
    if (!smallCounterStarted) {
      setSmallCounterStarted(true);
      decreaseAmount(10, 5000);
    }
    if (!bigCounterStarted) {
      setBigCounterStarted(true);
      decreaseAmount(20, 15000);
    }
  }, [isGapUpdated]);

  return (
    <GradientBorderCard borderType="top" gradient={theme.gradients.electricToDarkGradient({})}>
      <Spacing $size={17} />
      <div className="card-padding">
        {isAutopilot ? (
          <img
            alt="Oath on Autopilot"
            src={`${
              ENV_CONFIG().CLOUD_STORAGE.URL
            }/static_content/oath_logos/Oath_Autopilot_Dark.png`}
            className="autopilot-logo"
          />
        ) : (
          <>
            <Eyebrow $color={theme.colors.inkBlue}>Join the blue wave</Eyebrow>
            <Spacing $size={17} />
          </>
        )}
        <>
          {isAutopilot ? (
            <AutopilotCounter committed_amount={committed_amount || 0} />
          ) : (
            <Counter
              duration={smallCounterStarted ? 2 : 10}
              from={STARTING_GAP}
              number={gapAmountEstimate || STARTING_GAP}
            />
          )}
          <Spacing $size={17} />
          {isAutopilot ? (
            <span className="h4-text">committed this cycle and counting!</span>
          ) : (
            <span className="h4-text">is needed to close the gap for key downballot races</span>
          )}
        </>
      </div>
      <hr className="countdown-hr" />
      <div className="card-padding">
        {!isAutopilot && (
          <>
            <span className="body-text">
              Harris-Walz have already raised a historic $600+ million, while Democrats downballot
              are being outraised in races that cost <b>as little as $50K</b>. Make your dollar go
              further by supporting these competitive downballot races.
            </span>
            <Spacing $size={17} />
          </>
        )}
        <div className="countdown-link-row">
          {isAutopilot ? (
            issues?.map(issue => (
              <ButtonBrand
                key={issue.tab_value}
                onClick={() => onSelectIssue && onSelectIssue(issue.tab_value)}
                $backgroundColor={theme.colors.oathBlue}
                $width="100%"
              >
                {issue.tab_name}
              </ButtonBrand>
            ))
          ) : (
            <>
              <ButtonBrand
                to={getPathWithRef('/set?p=top-issues')}
                $backgroundColor={theme.colors.oathBlue}
              >
                Support by Issue
              </ButtonBrand>
              <ButtonBrand
                to={getPathWithRef('/set?p=states')}
                $backgroundColor={theme.colors.oathBlue}
              >
                Support by State
              </ButtonBrand>
            </>
          )}
        </div>
      </div>
    </GradientBorderCard>
  );
};
